import { PersistenceValidationError } from '../../application/errors/PersistenceError';
import type { RoutinePersistenceTransactions } from '../../application/ports/RoutinePersistenceTransactions';
import { TaskStatus } from '../../domain/routine/enums';
import type {
  DailyRoutine,
  DailyStep,
  RoutineStepTemplate,
  RoutineTemplate,
} from '../../domain/routine/models';
import type { DailyRoutineId, ProfileId } from '../../domain/shared/ids';
import type { AscendInvictusDatabase } from './AscendInvictusDatabase';
import {
  DailyRoutineMapper,
  DailyStepMapper,
  RoutineStepTemplateMapper,
  RoutineTemplateMapper,
} from './mappers';
import { executePersistence } from './translatePersistenceError';

export class DexieRoutinePersistenceTransactions implements RoutinePersistenceTransactions {
  public constructor(private readonly database: AscendInvictusDatabase) {}

  public async saveRoutineWithSteps(
    routine: DailyRoutine,
    steps: readonly DailyStep[],
  ): Promise<void> {
    const routineRecord = DailyRoutineMapper.toRecord(routine);
    const stepRecords = steps.map((step) => {
      if (step.dailyRoutineId !== routine.id || step.profileId !== routine.profileId) {
        throw new PersistenceValidationError();
      }
      return DailyStepMapper.toRecord(step);
    });

    await executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.dailyRoutines,
        this.database.dailySteps,
        async () => {
          await this.database.dailyRoutines.put(routineRecord);
          await this.database.dailySteps.where('dailyRoutineId').equals(routine.id).delete();
          await this.database.dailySteps.bulkPut(stepRecords);
        },
      );
    });
  }

  public createRoutineWithStepsIfAbsent(
    routine: DailyRoutine,
    steps: readonly DailyStep[],
  ): Promise<boolean> {
    const routineRecord = DailyRoutineMapper.toRecord(routine);
    const stepRecords = steps.map((step) => {
      if (step.dailyRoutineId !== routine.id || step.profileId !== routine.profileId) {
        throw new PersistenceValidationError();
      }
      return DailyStepMapper.toRecord(step);
    });

    return executePersistence('write', async () =>
      this.database.transaction(
        'rw',
        this.database.dailyRoutines,
        this.database.dailySteps,
        async () => {
          const existing = await this.database.dailyRoutines
            .where('[profileId+logicalDate]')
            .equals([routine.profileId, routine.logicalDate])
            .first();
          if (existing) return false;
          await this.database.dailyRoutines.add(routineRecord);
          await this.database.dailySteps.bulkAdd(stepRecords);
          return true;
        },
      ),
    );
  }

  public ensureTemplateWithSteps(
    template: RoutineTemplate,
    steps: readonly RoutineStepTemplate[],
  ): Promise<boolean> {
    const templateRecord = RoutineTemplateMapper.toRecord(template);
    const stepRecords = steps.map((step) => {
      if (step.routineTemplateId !== template.id || step.profileId !== template.profileId) {
        throw new PersistenceValidationError();
      }
      return RoutineStepTemplateMapper.toRecord(step);
    });

    return executePersistence('write', async () =>
      this.database.transaction(
        'rw',
        this.database.routineTemplates,
        this.database.routineStepTemplates,
        async () => {
          const activeTemplate = await this.database.routineTemplates
            .where('profileId')
            .equals(template.profileId)
            .filter((record) => record.active)
            .first();
          if (activeTemplate) return false;
          await this.database.routineTemplates.add(templateRecord);
          await this.database.routineStepTemplates.bulkAdd(stepRecords);
          return true;
        },
      ),
    );
  }

  public updateRoutineAndStep(routine: DailyRoutine, step: DailyStep): Promise<void> {
    if (step.dailyRoutineId !== routine.id || step.profileId !== routine.profileId) {
      throw new PersistenceValidationError();
    }
    const routineRecord = DailyRoutineMapper.toRecord(routine);
    const stepRecord = DailyStepMapper.toRecord(step);
    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.dailyRoutines,
        this.database.dailySteps,
        async () => {
          await this.database.dailyRoutines.put(routineRecord);
          await this.database.dailySteps.put(stepRecord);
        },
      );
    });
  }

  public postponeStep(
    sourceRoutine: DailyRoutine,
    sourceStep: DailyStep,
    targetRoutine: DailyRoutine,
    targetStep: DailyStep,
  ): Promise<void> {
    if (
      sourceStep.dailyRoutineId !== sourceRoutine.id ||
      targetStep.dailyRoutineId !== targetRoutine.id ||
      sourceRoutine.profileId !== targetRoutine.profileId ||
      sourceStep.profileId !== sourceRoutine.profileId ||
      targetStep.profileId !== targetRoutine.profileId ||
      sourceStep.postponedToStepId !== targetStep.id ||
      targetStep.postponedFromStepId !== sourceStep.id
    ) {
      throw new PersistenceValidationError();
    }
    const sourceRoutineRecord = DailyRoutineMapper.toRecord(sourceRoutine);
    const sourceStepRecord = DailyStepMapper.toRecord(sourceStep);
    const targetRoutineRecord = DailyRoutineMapper.toRecord(targetRoutine);
    const targetStepRecord = DailyStepMapper.toRecord(targetStep);

    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.dailyRoutines,
        this.database.dailySteps,
        async () => {
          const persistedSource = await this.database.dailySteps.get(sourceStep.id);
          if (
            !persistedSource ||
            persistedSource.status === TaskStatus.postponed ||
            persistedSource.postponedToStepId
          ) {
            throw new PersistenceValidationError();
          }
          await this.database.dailyRoutines.put(sourceRoutineRecord);
          await this.database.dailySteps.put(sourceStepRecord);
          await this.database.dailyRoutines.put(targetRoutineRecord);
          await this.database.dailySteps.add(targetStepRecord);
        },
      );
    });
  }

  public reorderSteps(
    routine: DailyRoutine,
    steps: readonly DailyStep[],
    templateSteps?: readonly RoutineStepTemplate[],
  ): Promise<void> {
    const stepIds = new Set<string>(steps.map((step) => step.id));
    const validSteps = steps.every(
      (step, index) =>
        step.dailyRoutineId === routine.id &&
        step.profileId === routine.profileId &&
        step.order === index,
    );
    const templateIds = new Set(templateSteps?.map((step) => step.id) ?? []);
    const validTemplates =
      !templateSteps ||
      (routine.templateId !== undefined &&
        templateSteps.every(
          (step, index) =>
            step.routineTemplateId === routine.templateId &&
            step.profileId === routine.profileId &&
            step.order === index,
        ));
    if (
      stepIds.size !== steps.length ||
      templateIds.size !== (templateSteps?.length ?? 0) ||
      !validSteps ||
      !validTemplates
    ) {
      throw new PersistenceValidationError();
    }

    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        this.database.dailySteps,
        this.database.routineStepTemplates,
        async () => {
          const persisted = await this.database.dailySteps
            .where('dailyRoutineId')
            .equals(routine.id)
            .toArray();
          if (
            persisted.length !== steps.length ||
            persisted.some((step) => !stepIds.has(step.id))
          ) {
            throw new PersistenceValidationError();
          }
          await this.database.dailySteps.where('dailyRoutineId').equals(routine.id).delete();
          await this.database.dailySteps.bulkAdd(
            steps.map((step) => DailyStepMapper.toRecord(step)),
          );
          if (templateSteps) {
            await this.database.routineStepTemplates
              .where('routineTemplateId')
              .equals(routine.templateId!)
              .delete();
            await this.database.routineStepTemplates.bulkAdd(
              templateSteps.map((step) => RoutineStepTemplateMapper.toRecord(step)),
            );
          }
        },
      );
    });
  }

  public listSteps(dailyRoutineId: DailyRoutineId): Promise<readonly DailyStep[]> {
    return executePersistence('read', async () => {
      const records = await this.database.dailySteps
        .where('dailyRoutineId')
        .equals(dailyRoutineId)
        .sortBy('order');
      return records.map((record) => DailyStepMapper.fromRecord(record));
    });
  }

  public deleteProfileData(profileId: ProfileId): Promise<void> {
    return executePersistence('write', async () => {
      await this.database.transaction(
        'rw',
        [
          this.database.profiles,
          this.database.routineTemplates,
          this.database.routineStepTemplates,
          this.database.dailyRoutines,
          this.database.dailySteps,
          this.database.journalEntries,
          this.database.workoutSessions,
          this.database.exerciseLogs,
          this.database.emotionalProtocolLogs,
          this.database.appPreferences,
          this.database.backupMetadata,
        ],
        async () => {
          await Promise.all([
            this.database.routineTemplates.where('profileId').equals(profileId).delete(),
            this.database.routineStepTemplates.where('profileId').equals(profileId).delete(),
            this.database.dailyRoutines.where('profileId').equals(profileId).delete(),
            this.database.dailySteps.where('profileId').equals(profileId).delete(),
            this.database.journalEntries.where('profileId').equals(profileId).delete(),
            this.database.workoutSessions.where('profileId').equals(profileId).delete(),
            this.database.exerciseLogs.where('profileId').equals(profileId).delete(),
            this.database.emotionalProtocolLogs.where('profileId').equals(profileId).delete(),
            this.database.appPreferences.where('profileId').equals(profileId).delete(),
            this.database.backupMetadata.where('profileId').equals(profileId).delete(),
          ]);
          await this.database.profiles.delete(profileId);
        },
      );
    });
  }
}
